import React from 'react';
import { Download } from 'lucide-react';
import { Link } from 'react-router-dom';

import {
  APK_DOWNLOAD_HREF,
  APK_SIZE_LABEL,
  APK_VERSION_LABEL,
  type SeoPageConfig,
  seoPages,
} from '../content/seoPages';

type SeoPageProps = {
  page: SeoPageConfig;
};

const relatedPages = seoPages.map(({ path, shortLabel, title }) => ({
  path,
  shortLabel,
  title,
}));

export const SeoPage: React.FC<SeoPageProps> = ({ page }) => {
  return (
    <div className="min-h-screen bg-[#f6f6f3] text-[#0f172a]">
      <div className="border-b border-black/5 bg-white/80 backdrop-blur-2xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-4 sm:px-6 md:px-8">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/logo-96.png?v=20260409a"
              alt="AR Drawing Master"
              className="h-10 w-10 rounded-2xl object-cover"
              width={96}
              height={96}
              loading="eager"
              decoding="async"
            />
            <div>
              <div className="font-display text-base font-semibold tracking-tight text-[#111111] sm:text-lg">AR Drawing Master</div>
              <div className="text-[11px] uppercase tracking-[0.24em] text-[#71717a]">Android App Guide</div>
            </div>
          </Link>

          <a
            href={APK_DOWNLOAD_HREF}
            download
            className="inline-flex items-center gap-2 rounded-full bg-[#111111] px-4 py-2.5 text-sm font-semibold text-white shadow-[0_18px_40px_rgba(15,23,42,0.14)]"
          >
            Download APK
            <Download size={16} />
          </a>
        </div>
      </div>

      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6 md:px-8 md:py-14">
        <section className="rounded-[2.2rem] border border-black/6 bg-[linear-gradient(135deg,#ffffff_0%,#f8fbff_50%,#f6f0ff_100%)] p-6 shadow-[0_24px_70px_rgba(15,23,42,0.08)] sm:p-8 md:p-12">
          <div className="max-w-3xl">
            <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#94a3b8]">{page.kicker}</div>
            <h1 className="mt-4 font-display text-[2.2rem] font-semibold tracking-[-0.06em] text-[#111111] sm:text-5xl">
              {page.heading}
            </h1>
            <p className="mt-5 text-[1rem] leading-7 text-[#52525b] sm:text-lg sm:leading-8">{page.intro}</p>

            <div className="mt-7 flex flex-wrap items-center gap-3 text-sm text-[#71717a]">
              <span className="rounded-full border border-black/6 bg-white px-3 py-1.5">{APK_VERSION_LABEL}</span>
              <span>{APK_SIZE_LABEL}</span>
              <span>Direct download on this domain</span>
            </div>
          </div>
        </section>

        <section className="mt-8 grid gap-8 lg:grid-cols-[1.2fr_0.8fr]">
          <div className="space-y-6">
            {page.sections.map((section) => (
              <article
                key={section.title}
                className="rounded-[1.8rem] border border-black/6 bg-white/92 p-6 shadow-[0_16px_45px_rgba(15,23,42,0.06)]"
              >
                <h2 className="font-display text-[1.6rem] font-semibold tracking-[-0.04em] text-[#111111] sm:text-[2rem]">
                  {section.title}
                </h2>
                <p className="mt-3 text-[0.98rem] leading-7 text-[#52525b] sm:text-[1.02rem]">{section.body}</p>
                {section.bullets ? (
                  <ul className="mt-4 space-y-2 text-[0.96rem] leading-7 text-[#3f3f46]">
                    {section.bullets.map((bullet) => (
                      <li key={bullet}>• {bullet}</li>
                    ))}
                  </ul>
                ) : null}
              </article>
            ))}

            {page.faqs ? (
              <section className="rounded-[1.8rem] border border-black/6 bg-white/92 p-6 shadow-[0_16px_45px_rgba(15,23,42,0.06)]">
                <h2 className="font-display text-[1.6rem] font-semibold tracking-[-0.04em] text-[#111111] sm:text-[2rem]">
                  Common questions
                </h2>
                <div className="mt-5 space-y-3">
                  {page.faqs.map((faq) => (
                    <details
                      key={faq.question}
                      className="rounded-2xl border border-black/6 bg-[#fafaf8] px-5 py-4"
                    >
                      <summary className="cursor-pointer list-none font-semibold text-[#111111]">{faq.question}</summary>
                      <p className="mt-3 text-sm leading-7 text-[#52525b]">{faq.answer}</p>
                    </details>
                  ))}
                </div>
              </section>
            ) : null}
          </div>

          <aside className="space-y-6">
            <div className="rounded-[1.8rem] border border-black/6 bg-[#111827] p-6 text-white shadow-[0_24px_70px_rgba(15,23,42,0.16)]">
              <div className="text-xs font-semibold uppercase tracking-[0.28em] text-white/50">Install</div>
              <h2 className="mt-4 font-display text-[1.8rem] font-semibold tracking-[-0.05em]">
                {page.ctaTitle}
              </h2>
              <p className="mt-4 text-[0.98rem] leading-7 text-white/72">{page.ctaBody}</p>
              <a
                href={APK_DOWNLOAD_HREF}
                download
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-white px-5 py-3.5 text-sm font-semibold text-[#111111]"
              >
                Download APK
                <Download size={16} />
              </a>
            </div>

            <div className="rounded-[1.8rem] border border-black/6 bg-white/92 p-6 shadow-[0_16px_45px_rgba(15,23,42,0.06)]">
              <div className="text-xs font-semibold uppercase tracking-[0.28em] text-[#94a3b8]">Explore More</div>
              <div className="mt-4 space-y-3">
                <Link to="/" className="block rounded-2xl border border-black/6 px-4 py-3 font-semibold text-[#111111] transition hover:bg-[#fafaf8]">
                  Homepage
                </Link>
                {relatedPages
                  .filter((item) => item.path !== page.path)
                  .map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className="block rounded-2xl border border-black/6 px-4 py-3 text-sm text-[#3f3f46] transition hover:bg-[#fafaf8]"
                    >
                      <span className="font-semibold text-[#111111]">{item.shortLabel}</span>
                      <span className="mt-1 block">{item.title}</span>
                    </Link>
                  ))}
                <Link to="/privacy" className="block rounded-2xl border border-black/6 px-4 py-3 text-sm text-[#3f3f46] transition hover:bg-[#fafaf8]">
                  <span className="font-semibold text-[#111111]">Privacy</span>
                  <span className="mt-1 block">Read the AR Drawing Master privacy policy.</span>
                </Link>
              </div>
            </div>
          </aside>
        </section>
      </main>
    </div>
  );
};
